import { durationService, useProjectDurationSettings } from "@oculai/planner";
import { format, isDate, parseISO, subHours } from "date-fns";
import { ref, shallowRef } from "vue";
import { useI18n } from "vue-i18n";
import { Column, Data } from "@/components/UI/Table/types";
import { apiClient } from "@/repositories/clients";
import { ShortenedProcess, TagGroupedProcess } from "@/types/Process";
import {
  calculateActiveDuration,
  calculateWorkHoursSum,
  filterColumnDateField,
  filterLocationField,
  filterProcessField,
} from "@/views/analytics/ProcessTable/utils";
import LocationTag from "../partials/LocationTag.vue";

export const usePlainPresets = () => {
  const { t } = useI18n();

  const { projectDurationSettings } = useProjectDurationSettings(apiClient);

  const durationSettings = projectDurationSettings.value
    ? durationService.calculateSettings(
        projectDurationSettings.value.working_hours,
        projectDurationSettings.value.non_working_days,
      )
    : null;

  const locationTag = shallowRef(LocationTag);

  const durationData = ref({ netto: { days: 0, hours: 0 } });

  const columns: Column[] = [
    {
      label: t("analytics.processes.process"),
      field: "process",
      size: 3,
      filters: [
        {
          type: "process",
          filterFn: filterProcessField,
          filterValue: { processes: [], processTypes: [] } as Record<
            "processes" | "processTypes",
            string[]
          >,
          placeholder: t("analytics.processes.placeholder_process_type"),
        },
      ],
      class: "min-w-64",
    },
    {
      label: t("analytics.processes.location"),
      field: "location",
      size: 5,
      class: "min-w-72",
      filters: [
        {
          type: "location",
          filterFn: filterLocationField,
          filterValue: {
            building: [] as string[],
            level: [] as string[],
            section: [] as string[],
            component: [] as string[],
          },
          placeholder: t("analytics.processes.placeholder_hierarchy_tag"),
        },
      ],
      renderComponent: locationTag,
      toggleSort: (a: unknown, b: unknown) => {
        const firstEl = a as Record<string, string>;
        const secondEl = b as Record<string, string>;
        return firstEl[Object.keys(firstEl)[0]].localeCompare(secondEl[Object.keys(secondEl)[0]]);
      },
    },

    {
      label: t("analytics.processes.date"),
      field: "date",
      size: 2,
      render: (value) => (isDate(value) ? format(value as Date, "dd.MM.yyyy") : "--"),
      filters: [
        {
          type: "date",
          filterFn: (v, i) => filterColumnDateField(v, i, "date"),
          filterValue: [],
          placeholder: "Filter",
        },
      ],
      sortDirection: "asc",
      class: "max-w-48",
    },
    {
      label: t("analytics.processes.duration"),
      field: "duration",
      render: (value) => (isDate(value) ? format(value as Date, "HH:mm") : "--"),
      filterDisabled: true,
      class: "max-w-32",
    },
    {
      label: t("analytics.processes.working_hours"),
      field: "working_hours",
      render: (value) => ((value as number) > 0 ? `${value}` : "--"),
      filterDisabled: true,
      class: "max-w-32",
    },
  ];

  const getSummary = (data: Data[]) => {
    let duration = 0;
    let working_hours = 0;

    data.forEach((item) => {
      duration += (item.duration as Date).getTime() + 60 * 60 * 1000;
      working_hours += item.working_hours as number;
    });

    return {
      process: `${data.length} ${t("analytics.processes.item", data.length)}`,
      duration: `Σ ${duration / 1000 / 60 / 60} ${t("time.hour", { count: working_hours })}`,
      working_hours: `Σ ${working_hours} ${t("time.hour", { count: working_hours })}`,
    };
  };

  const dataMapper = (processData: TagGroupedProcess[]) => {
    const translationString = "report.sections.processes.process_type_entries";

    const mappedData = processData.flatMap<
      Data & Record<(typeof columns)[number]["field"], unknown>
    >((item) => {
      const itemProcesses = item.processes
        .slice()
        .sort((a, b) => a.start_time.getTime() - b.start_time.getTime());

      return itemProcesses.map((process) => {
        const tagOrder = {
          building: 1,
          level: 2,
          section: 3,
          component: 4,
        };
        const location = {} as Record<string, string>;
        for (const tag in tagOrder) {
          if (Object.prototype.hasOwnProperty.call(item.tags, tag)) {
            location[tag] = item.tags[tag as keyof typeof item.tags];
          }
        }
        return {
          ...process,
          ...item.tags,
          process: t(`${translationString}.${process.encoded_label}`),
          date: parseISO(process.date),
          duration: subHours(process.end_time.getTime() - process.start_time.getTime(), 1),
          working_hours: calculateWorkHoursSum([process], true),
          location: location,
        };
      });
    });

    if (durationSettings) {
      const nettoDuration = calculateActiveDuration(
        durationSettings,
        mappedData as ShortenedProcess[],
      );

      durationData.value = { netto: nettoDuration };
    }

    return mappedData;
  };

  return {
    columns,
    dataMapper,
    getSummary,
  };
};
